import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Collapse,
  IconButton,
  Drawer,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  Divider,
  ListItemButton,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import TaskIcon from "@mui/icons-material/Task";
import ErrorIcon from "@mui/icons-material/Error";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ScienceIcon from "@mui/icons-material/Science";
import HistoryIcon from "@mui/icons-material/History";
import GavelIcon from "@mui/icons-material/Gavel";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import InsightsIcon from "@mui/icons-material/Insights";
import EditIcon from "@mui/icons-material/Edit";
import SecurityIcon from "@mui/icons-material/Security";
import DescriptionIcon from "@mui/icons-material/Description";
import BusinessIcon from "@mui/icons-material/Business";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import EcoIcon from "@mui/icons-material/EnergySavingsLeaf";
import PublicIcon from "@mui/icons-material/Public";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import WifiIcon from "@mui/icons-material/Wifi";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ListAltIcon from "@mui/icons-material/ListAlt";
import { useApp } from "../context/AppContext";
import { useFeatures } from "../hooks/useFeatures";
import { fetchApi } from "../services/api";
import { api } from "../services/api";
import ProjectChangeModal from "./ProjectChangeModal";
import SelectProjectPrompt from "./SelectProjectPrompt";
import { workflowTemplateApi } from "../services/workflowApi";
import { isCertBuddyMember } from "../utils/certbuddyMember";

const DRAWER_WIDTH = 250;
const COLLAPSED_WIDTH = 65; // Width when showing only icons

// Agency icon mapping
const AGENCY_ICONS = {
  EPA: EcoIcon,
  CARB: PublicIcon,
  NHTSA: HealthAndSafetyIcon,
  FCC: WifiIcon,
  FAA: FlightTakeoffIcon,
  "FAA-AST": FlightTakeoffIcon,
  USCG: PublicIcon,
  IMO: PublicIcon,
  NASA: FlightTakeoffIcon,
};

function Sidebar({
  isOpen,
  onToggle,
  codifierOpen,
  setCodifierOpen,
  approverOpen,
  setApproverOpen,
  reporterOpen,
  setReporterOpen,
  applicatorOpen,
  setApplicatorOpen,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    codifierOpen: _appCodifierOpen,
    setCodifierOpen: _setAppCodifierOpen,
    approverOpen: _appApproverOpen,
    setApproverOpen: _setAppApproverOpen,
    reporterOpen: _appReporterOpen,
    setReporterOpen: _setAppReporterOpen,
    applicatorOpen: _appApplicatorOpen,
    setApplicatorOpen: _setAppApplicatorOpen,
    currentProject,
    setCurrentProject,
    currentProjectId,
    setCurrentProjectId,
    PROJECT_TYPE_ICONS,
    user,
    organization,
    featureFlags,
    projects: appProjects,
  } = useApp();
  const { faa, automotive } = useFeatures(); // Hook into feature gating
  const [_manageProjectsOpen, _setManageProjectsOpen] = useState(false);
  // the app shell already loads the list; a second fetch here doubled it on every page load
  const projects = Array.isArray(appProjects) ? appProjects : [];
  const [_conversation, setConversation] = useState(null);
  const [_isEditingName, _setIsEditingName] = useState(false);
  const [_editedName, setEditedName] = useState("");
  const [projectChangeModal, setProjectChangeModal] = useState({
    open: false,
    project: null,
  });
  const [supplierProfile, setSupplierProfile] = useState(null);
  const [taskSystemConfig, setTaskSystemConfig] = useState(null);
  const [_loadingTaskConfig, setLoadingTaskConfig] = useState(false);
  const isFetchingTaskConfig = useRef(false);

  const [showSelectProjectPrompt, setShowSelectProjectPrompt] = useState(false);

  // Available agencies based on templates
  const [availableAgencies, setAvailableAgencies] = useState([]);
  const [agentStudioOpen, setAgentStudioOpen] = useState(true);

  // Fetch supplier profile if user is a supplier
  useEffect(() => {
    const fetchSupplierProfile = async () => {
      if (user?.role === "cbSupplier") {
        try {
          const profile = await api.getMyProfile();
          setSupplierProfile(profile);
        } catch (err) {
          console.error("Error fetching supplier profile:", err);
        }
      }
    };

    fetchSupplierProfile();
  }, [user]);

  // Fetch task system configuration
  const fetchTaskSystemConfig = useCallback(async () => {
    if (user?.role !== "cbSupplier" && !isFetchingTaskConfig.current) {
      isFetchingTaskConfig.current = true;
      setLoadingTaskConfig(true);
      try {
        const config = await api.getTaskSystemConfig();
        setTaskSystemConfig(config);
      } catch (err) {
        console.error("Error fetching task system config:", err);
      } finally {
        setLoadingTaskConfig(false);
        isFetchingTaskConfig.current = false;
      }
    }
  }, [user?.role]);

  useEffect(() => {
    fetchTaskSystemConfig();
  }, [fetchTaskSystemConfig]);

  // Reset the fetching ref when user changes
  useEffect(() => {
    isFetchingTaskConfig.current = false;
  }, [user]);

  // Styled component for not developed items
  const NotDevelopedItem = React.forwardRef(({ children, ...props }, ref) => {
    const isDemoMode = useMemo(() => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");
        const orgId = user?.org_id;
        const role = user?.role;
        const demoModeEnabled = localStorage.getItem("demoMode") === "true";

        // Only enable demo mode for eligible users (org_id='o1' and role='cbProgram') who have it enabled
        return demoModeEnabled && orgId === "o1" && role === "cbProgram";
      } catch (e) {
        return false;
      }
    }, []);

    const demoStyle = {
      position: "relative",
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.04)",
        pointerEvents: "none",
        borderRadius: "4px",
        display: isDemoMode ? "none" : "block",
        cursor: isDemoMode ? "pointer" : "not-allowed",
      },
      "& .MuiListItemIcon-root": {
        opacity: isDemoMode ? 1 : 0.6,
      },
      "& .MuiTypography-root": {
        color: (theme) => (isDemoMode ? theme.palette.text.primary : theme.palette.text.disabled),
        "& .MuiTypography-caption": {
          display: isDemoMode ? "none" : "inline",
        },
      },
      "&:hover": {
        "&::after": {
          backgroundColor: isDemoMode ? "transparent" : "rgba(0, 0, 0, 0.08)",
        },
        cursor: isDemoMode ? "pointer" : "not-allowed",
      },
      pointerEvents: isDemoMode ? "auto" : "none",
    };

    if (isDemoMode) {
      // In demo mode, render normally without any restrictions
      return (
        <Box ref={ref} {...props}>
          {React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, {
                disabled: false,
                sx: {
                  ...(child.props.sx || {}),
                  cursor: "pointer",
                  pointerEvents: "auto",
                  "&.Mui-disabled": {
                    opacity: 1,
                    pointerEvents: "auto",
                  },
                },
              });
            }
            return child;
          })}
        </Box>
      );
    }

    return (
      <Box ref={ref} sx={demoStyle} {...props}>
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              disabled: true,
              sx: {
                ...(child.props.sx || {}),
                cursor: "not-allowed",
                pointerEvents: "none",
                "&.Mui-disabled": {
                  opacity: 1,
                  pointerEvents: "none",
                },
              },
            });
          }
          return child;
        })}
      </Box>
    );
  });

  const handleCodifierClick = () => {
    setCodifierOpen(!codifierOpen);
  };

  const handleApproverClick = () => {
    setApproverOpen(!approverOpen);
  };

  const handleReporterClick = () => {
    setReporterOpen(!reporterOpen);
  };

  const handleApplicatorClick = () => {
    setApplicatorOpen(!applicatorOpen);
  };

  const handleNavClick = (e) => {
    if (!currentProjectId) {
      e.preventDefault();
      setShowSelectProjectPrompt(true);
    }
  };

  const handleProjectChange = (e) => {
    if (e.target.value === "manage") {
      navigate("/manage-projects");
      return;
    }

    // Use project.id instead of project.title
    const selectedProject = projects.find((p) => p.id === e.target.value);
    if (selectedProject) {
      setProjectChangeModal({
        open: true,
        project: {
          id: selectedProject.id,
          name: selectedProject.title,
          type: selectedProject.projectType,
          status: selectedProject.status || "active",
        },
      });
    }
  };

  const handleProjectChangeConfirm = () => {
    const { project } = projectChangeModal;
    if (project) {
      setCurrentProject(project);
      setCurrentProjectId(project.id);
      navigate(`/project/${project.id}/dashboard`);
    }
    setProjectChangeModal({ open: false, project: null });
  };

  const handleOpenWorkflowList = (agency) => {
    const projectId = currentProjectId || currentProject?.id || projects[0]?.id;
    if (!projectId) {
      console.warn("[Sidebar] No project selected — cannot open workflow for agency:", agency);
      return;
    }
    navigate(`/project/${projectId}/process-flow?agency=${agency}`);
  };

  const _handleMenuItemClick = (event, callback) => {
    // Prevent the Select from changing value
    event.stopPropagation();
    callback();
  };

  // Sync current project from URL — runs only when the URL project segment changes
  useEffect(() => {
    if (projects.length === 0) {
      return;
    }

    const pathParts = location.pathname.split("/");
    const isProjectUrl = pathParts[1] === "project";
    const urlProjectId = isProjectUrl ? pathParts[2] : null;

    if (urlProjectId) {
      if (urlProjectId === currentProjectId) {
        return;
      }
      const projectFromUrl = projects.find((p) => p.id === urlProjectId);
      if (projectFromUrl && projectFromUrl.id !== currentProjectId) {
        setCurrentProject({
          id: projectFromUrl.id,
          name: projectFromUrl.title,
          type: projectFromUrl.projectType,
          status: projectFromUrl.status || "active",
        });
        setCurrentProjectId(projectFromUrl.id);
      }
    } else if (!currentProjectId && projects.length > 0) {
      const defaultProject = projects[0];
      if (defaultProject.id !== currentProjectId) {
        setCurrentProject({
          id: defaultProject.id,
          name: defaultProject.title,
          type: defaultProject.projectType,
          status: defaultProject.status || "active",
        });
        setCurrentProjectId(defaultProject.id);
      }
    }
  }, [location.pathname, projects, currentProjectId]);

  useEffect(() => {
    const fetchConversation = async () => {
      const selectedProject = projects.find((p) => p.title === currentProject);
      if (selectedProject?.id) {
        try {
          const data = await fetchApi(`/api/conversations/project/${selectedProject.id}`);
          setConversation(data);
          setEditedName(data.conversation_name);
        } catch (error) {
          console.error("Error fetching conversation:", error);
          setConversation(null);
          setEditedName("");
        }
      } else {
        setConversation(null);
        setEditedName("");
      }
    };

    fetchConversation();
  }, [currentProject, projects]);

  const isDemoMode = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      const orgId = user?.org_id;
      const role = user?.role;
      const demoModeEnabled = localStorage.getItem("demoMode") === "true";

      // Only enable demo mode for eligible users (org_id='o1' and role='cbProgram') who have it enabled
      return demoModeEnabled && orgId === "o1" && role === "cbProgram";
    } catch (e) {
      return false;
    }
  }, []);

  useEffect(() => {
    const handleProjectChange = (event) => {
      const projectId = event.detail.projectId;
      setCurrentProjectId(projectId);
    };

    window.addEventListener("projectChanged", handleProjectChange);
    return () => {
      window.removeEventListener("projectChanged", handleProjectChange);
    };
  }, [setCurrentProjectId]);

  // Fetch available agencies based on organization type
  useEffect(() => {
    const fetchAvailableAgencies = async () => {
      if (!organization?.type) {
        return;
      }

      try {
        // Map organization type to domain for template filtering
        const domainMapping = {
          automotive: "auto",
          airplanes: "airplanes",
          aerospace: "aerospace",
          naval: "naval",
        };

        const domain = domainMapping[organization.type] || organization.type;

        // Fetch templates for the mapped domain
        const templates = await workflowTemplateApi.getTemplates({ domain });

        // Extract unique agencies
        const agencies = [...new Set(templates.map((t) => t.agency))].filter(Boolean).sort();
        setAvailableAgencies(agencies);
      } catch (err) {
        console.error("Error fetching agencies:", err);
        setAvailableAgencies([]);
      }
    };

    fetchAvailableAgencies();
  }, [organization?.type]);

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: isOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH,
          flexShrink: 0,
          height: "100%",
          position: "relative",
          "& .MuiDrawer-paper": {
            position: "relative",
            width: isOpen ? DRAWER_WIDTH : COLLAPSED_WIDTH,
            boxSizing: "border-box",
            backgroundColor: "#eeeffa",
            transition: "width 0.2s ease-in-out",
            overflowX: "hidden",
            borderRight: "1px solid rgba(0,0,0,0.12)",
            display: "flex",
            flexDirection: "column",
            height: "100%",
            fontFamily: "Roboto, sans-serif",
            "& .MuiTypography-root": {
              fontFamily: "Roboto, sans-serif",
            },
            "& .MuiListItemText-root": {
              "& .MuiTypography-root": {
                fontFamily: "Roboto, sans-serif",
              },
            },
          },
          "& .agent-group-item": {
            fontSize: "0.775rem",
            fontWeight: 800,
          },
        }}
      >
        {/* Project Selector - Hide for suppliers */}
        {user?.role !== "cbSupplier" && (
          <Box sx={{ p: isOpen ? 2 : 1, minHeight: 50, borderBottom: "1px solid #e0e0e0" }}>
            {isOpen && (
              <FormControl fullWidth size="small">
                <Select
                  value={
                    currentProject?.id && projects.some((p) => p.id === currentProject.id)
                      ? currentProject.id
                      : ""
                  } // Only use ID if it exists in projects
                  onChange={handleProjectChange}
                  displayEmpty
                  IconComponent={KeyboardArrowDownIcon}
                  sx={{
                    backgroundColor: "#fff",
                    "& .MuiSelect-select": { py: 1 },
                  }}
                >
                  <MenuItem disabled value="">
                    <em>Select a project</em>
                  </MenuItem>
                  {Array.isArray(projects) &&
                    projects.map((project) => (
                      <MenuItem key={project.id} value={project.id}>
                        {" "}
                        {/* Use ID as value */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            width: "100%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              minWidth: 0,
                            }}
                          >
                            {React.createElement(
                              PROJECT_TYPE_ICONS[project.projectType] || PROJECT_TYPE_ICONS.default,
                              {
                                sx: { fontSize: "1.2rem" },
                              }
                            )}
                            <Tooltip title={`${project.title} (${project.id})`} arrow>
                              <Typography
                                sx={{
                                  ml: 1,
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  maxWidth: "200px",
                                }}
                              >
                                {project.title}
                                {/* Show ID for duplicate names */}
                                {projects.filter((p) => p.title === project.title).length > 1 && (
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    sx={{
                                      ml: 1,
                                      color: "text.secondary",
                                      fontStyle: "italic",
                                    }}
                                  >
                                    ({project.id.split("-")[0]})
                                  </Typography>
                                )}
                              </Typography>
                            </Tooltip>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))}
                  <MenuItem value="manage" sx={{ color: "primary.main" }}>
                    <EditIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
                    Manage Projects
                  </MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        )}

        {/* Navigation List */}
        <List
          sx={{
            flex: 1,
            overflow: "auto",
            pt: 2,
            "& .MuiListItem-root": {
              py: 0.5, // Reduced vertical padding for compactness
              minHeight: 36, // Reduced minimum height
              "&.Mui-selected": {
                backgroundColor: "#5664d2 !important", // High contrast background for selected item
                color: "white",
                "&:hover": {
                  backgroundColor: "#4655c8 !important", // Darker on hover
                },
                "& .MuiListItemIcon-root": {
                  color: "white",
                },
                "& .MuiTypography-root": {
                  color: "white",
                  fontWeight: 600,
                },
              },
            },
          }}
        >
          {/* Supplier Navigation - Only show if feature flag is enabled */}
          {featureFlags.SUPPLIER_PORTAL_ENABLED && user?.role === "cbSupplier" ? (
            <>
              <Tooltip title="Onboarding" placement="right" arrow disableHoverListener={isOpen}>
                <ListItem
                  button
                  component={Link}
                  to="/supplier-onboarding"
                  selected={location.pathname === "/supplier-onboarding"}
                  sx={{ px: isOpen ? 2 : "14px" }}
                >
                  <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                    <HowToRegIcon />
                  </ListItemIcon>
                  {isOpen && <ListItemText primary="Onboarding" />}
                </ListItem>
              </Tooltip>

              {/* Only show these options if onboarding is completed */}
              {supplierProfile?.onboarding_status === "Completed" && (
                <>
                  <Tooltip title="Dashboard" placement="right" arrow disableHoverListener={isOpen}>
                    <ListItem
                      button
                      component={Link}
                      to="/supplier-portal"
                      selected={location.pathname === "/supplier-portal"}
                      sx={{ px: isOpen ? 2 : "14px" }}
                    >
                      <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                        <DashboardIcon />
                      </ListItemIcon>
                      {isOpen && <ListItemText primary="Dashboard" />}
                    </ListItem>
                  </Tooltip>

                  <Tooltip title="Tasks" placement="right" arrow disableHoverListener={isOpen}>
                    <ListItem
                      button
                      component={Link}
                      to="/supplier-tasks"
                      selected={location.pathname === "/supplier-tasks"}
                      sx={{ px: isOpen ? 2 : "14px" }}
                    >
                      <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                        <AssignmentIcon />
                      </ListItemIcon>
                      {isOpen && <ListItemText primary="Tasks" />}
                    </ListItem>
                  </Tooltip>

                  <Tooltip title="Workflows" placement="right" arrow disableHoverListener={isOpen}>
                    <ListItem
                      button
                      component={Link}
                      to="/supplier-workflows"
                      selected={location.pathname === "/supplier-workflows"}
                      sx={{ px: isOpen ? 2 : "14px" }}
                    >
                      <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                        <AccountTreeIcon />
                      </ListItemIcon>
                      {isOpen && <ListItemText primary="Workflows" />}
                    </ListItem>
                  </Tooltip>

                  <Tooltip title="Documents" placement="right" arrow disableHoverListener={isOpen}>
                    <ListItem
                      button
                      component={Link}
                      to="/supplier-documents"
                      selected={location.pathname === "/supplier-documents"}
                      sx={{ px: isOpen ? 2 : "14px" }}
                    >
                      <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                        <DescriptionIcon />
                      </ListItemIcon>
                      {isOpen && <ListItemText primary="Documents" />}
                    </ListItem>
                  </Tooltip>

                  <Tooltip title="CertGPT" placement="right" arrow disableHoverListener={isOpen}>
                    <ListItem
                      button
                      component={Link}
                      to="/supplier-CertGPT"
                      selected={location.pathname === "/supplier-CertGPT"}
                      sx={{ px: isOpen ? 2 : "14px" }}
                    >
                      <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                        <ScienceIcon />
                      </ListItemIcon>
                      {isOpen && <ListItemText primary="CertGPT" />}
                    </ListItem>
                  </Tooltip>
                </>
              )}

              {/* Show restricted message if onboarding not completed */}
              {supplierProfile && supplierProfile.onboarding_status !== "Completed" && isOpen && (
                <ListItem sx={{ px: 2, py: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    Complete onboarding to access more features
                  </Typography>
                </ListItem>
              )}
            </>
          ) : !featureFlags.SUPPLIER_PORTAL_ENABLED && user?.role === "cbSupplier" ? (
            <ListItem sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>
                Supplier portal is currently disabled
              </Typography>
            </ListItem>
          ) : (
            <>
              <Tooltip title="Project Home" placement="right" arrow disableHoverListener={isOpen}>
                <ListItem
                  button
                  component={Link}
                  to={currentProjectId ? `/project/${currentProjectId}/dashboard` : "#"}
                  onClick={handleNavClick}
                  selected={location.pathname === `/project/${currentProjectId}/dashboard`}
                  sx={{ px: isOpen ? 2 : "14px" }}
                >
                  <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                    <HomeIcon />
                  </ListItemIcon>
                  {isOpen && <ListItemText primary="Project Home" />}
                </ListItem>
              </Tooltip>

              <Tooltip title="CertGPT" placement="right" arrow disableHoverListener={isOpen}>
                <ListItemButton onClick={handleCodifierClick}>
                  {isOpen && (
                    <>
                      <ListItemText
                        primary="CertGPT"
                        primaryTypographyProps={{
                          className: "agent-group-item",
                        }}
                      />
                      {codifierOpen ? <ExpandLess /> : <ExpandMore />}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>

              <Collapse in={codifierOpen} timeout="auto">
                <List component="div" disablePadding>
                  <Tooltip title="CertGPT" placement="right" arrow disableHoverListener={isOpen}>
                    <ListItem
                      button
                      component={Link}
                      to={currentProjectId ? `/project/${currentProjectId}/CertGPT` : "#"}
                      onClick={handleNavClick}
                      selected={location.pathname === `/project/${currentProjectId}/CertGPT`}
                      sx={{ px: isOpen ? 2 : "14px" }}
                    >
                      <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                        {React.createElement(
                          PROJECT_TYPE_ICONS[currentProject?.type?.toLowerCase()] || DescriptionIcon
                        )}
                      </ListItemIcon>
                      {isOpen && <ListItemText primary="CertGPT" />}
                    </ListItem>
                  </Tooltip>
                </List>
              </Collapse>

              <Tooltip title="Agent Reporter" placement="right" arrow disableHoverListener={isOpen}>
                <ListItemButton onClick={handleReporterClick}>
                  {isOpen && (
                    <>
                      <ListItemText
                        primary="AGENT REPORTER"
                        primaryTypographyProps={{
                          className: "agent-group-item",
                        }}
                      />
                      {reporterOpen ? <ExpandLess /> : <ExpandMore />}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>

              <Collapse in={reporterOpen} timeout="auto">
                <List component="div" disablePadding>
                  <Tooltip
                    title={taskSystemConfig?.task_system === "builtin" ? "Tasks" : "Jira Tasks"}
                    placement="right"
                    arrow
                    disableHoverListener={isOpen}
                  >
                    <ListItem
                      button
                      component={Link}
                      to={
                        taskSystemConfig?.task_system === "builtin"
                          ? `/project/${currentProjectId}/tasks`
                          : `/project/${currentProjectId}/jira-tasks`
                      }
                      selected={
                        location.pathname ===
                        (taskSystemConfig?.task_system === "builtin"
                          ? `/project/${currentProjectId}/tasks`
                          : `/project/${currentProjectId}/jira-tasks`)
                      }
                      sx={{
                        px: isOpen ? 2 : "14px",
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                        <TaskIcon />
                      </ListItemIcon>
                      {isOpen && (
                        <ListItemText
                          primary={
                            taskSystemConfig?.task_system === "builtin" ? "Tasks" : "Jira Tasks"
                          }
                        />
                      )}
                    </ListItem>
                  </Tooltip>

                  <Tooltip
                    title="Risks & Errors"
                    placement="right"
                    arrow
                    disableHoverListener={isOpen}
                  >
                    <ListItem
                      button
                      onClick={async () => {
                        // First navigate to the page
                        navigate(`/project/${currentProjectId}/risks-and-errors`);
                      }}
                      selected={
                        location.pathname === `/project/${currentProjectId}/risks-and-errors`
                      }
                      sx={{
                        px: isOpen ? 2 : "14px",
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: isOpen ? 40 : "auto",
                          position: "relative",
                        }}
                      >
                        <ErrorIcon />
                      </ListItemIcon>
                      {isOpen && (
                        <Box
                          sx={{
                            display: "flex",
                            flex: 1,
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <ListItemText primary="Risks & Errors" />
                          {/* <Badge badgeContent={3} color="error" sx={{ mr: 1 }} /> */}
                        </Box>
                      )}
                    </ListItem>
                  </Tooltip>
                  <Tooltip title="Reports" placement="right" arrow disableHoverListener={isOpen}>
                    <ListItem
                      button
                      component={Link}
                      onClick={handleNavClick}
                      to={currentProjectId ? `/project/${currentProjectId}/reports` : "#"}
                      selected={location.pathname === `/project/${currentProjectId}/reports`}
                      sx={{
                        px: isOpen ? 2 : "14px",
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                        <AssessmentIcon />
                      </ListItemIcon>
                      {isOpen && <ListItemText primary="Reports" />}
                    </ListItem>
                  </Tooltip>
                </List>
              </Collapse>

              {/* AGENT STUDIO — its own bold section (like Codifier / Reporter) */}
              <Tooltip title="Agent Studio" placement="right" arrow disableHoverListener={isOpen}>
                <ListItemButton onClick={() => setAgentStudioOpen(!agentStudioOpen)}>
                  {isOpen && (
                    <>
                      <ListItemText
                        primary="AGENT STUDIO"
                        primaryTypographyProps={{
                          className: "agent-group-item",
                        }}
                      />
                      {agentStudioOpen ? <ExpandLess /> : <ExpandMore />}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>

              <Collapse in={agentStudioOpen} timeout="auto">
                <List component="div" disablePadding>
                  <Tooltip
                    title="Agent Studio"
                    placement="right"
                    arrow
                    disableHoverListener={isOpen}
                  >
                    <ListItem
                      button
                      component={Link}
                      onClick={handleNavClick}
                      to="/agents"
                      selected={location.pathname.startsWith("/agents")}
                      sx={{ px: isOpen ? 2 : "14px" }}
                    >
                      <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                        <SmartToyIcon />
                      </ListItemIcon>
                      {isOpen && <ListItemText primary="Agents" />}
                    </ListItem>
                  </Tooltip>
                  <Tooltip title="Artifacts" placement="right" arrow disableHoverListener={isOpen}>
                    <ListItem
                      button
                      component={Link}
                      onClick={handleNavClick}
                      to="/artifacts"
                      selected={location.pathname.startsWith("/artifacts")}
                      sx={{ px: isOpen ? 2 : "14px" }}
                    >
                      <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                        <DescriptionIcon />
                      </ListItemIcon>
                      {isOpen && <ListItemText primary="Artifacts" />}
                    </ListItem>
                  </Tooltip>
                  {featureFlags.MARKET_INTEL_ENABLED && (
                    <Tooltip
                      title="Market Intelligence"
                      placement="right"
                      arrow
                      disableHoverListener={isOpen}
                    >
                      <ListItem
                        button
                        component={Link}
                        onClick={handleNavClick}
                        to="/market-intel"
                        selected={location.pathname.startsWith("/market-intel")}
                        sx={{ px: isOpen ? 2 : "14px" }}
                      >
                        <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                          <PublicIcon />
                        </ListItemIcon>
                        {isOpen && <ListItemText primary="Market Intelligence" />}
                      </ListItem>
                    </Tooltip>
                  )}
                </List>
              </Collapse>

              {/* Dynamic Compliance Workflows - Based on Templates */}
              {availableAgencies.length > 0 && (
                <>
                  <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.08)" }} />
                  {isOpen && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ px: 2, py: 1, display: "block", fontWeight: 700 }}
                    >
                      COMPLIANCE WORKFLOWS
                    </Typography>
                  )}

                  {/* Dynamic Agency Workflows from Templates */}
                  {availableAgencies.map((agency) => {
                    const AgencyIcon = AGENCY_ICONS[agency] || AccountTreeIcon;
                    return (
                      <Tooltip
                        key={agency}
                        title={`${agency} Workflows`}
                        placement="right"
                        arrow
                        disableHoverListener={isOpen}
                      >
                        <ListItem
                          button
                          onClick={() => handleOpenWorkflowList(agency)}
                          sx={{ px: isOpen ? 2 : "14px" }}
                        >
                          <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                            <AgencyIcon />
                          </ListItemIcon>
                          {isOpen && <ListItemText primary={agency} />}
                        </ListItem>
                      </Tooltip>
                    );
                  })}
                </>
              )}

              {/* FAA Certification Section - Gated by Domain */}
              {faa.enabled && (
                <>
                  <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.08)" }} />
                  {isOpen && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ px: 2, py: 1, display: "block", fontWeight: 700 }}
                    >
                      FAA CERTIFICATION
                    </Typography>
                  )}

                  {/* Dashboard - Now smart routed via standard dashboard link, but specific FAA items below */}

                  {/* Vehicle Classification - Operators Only */}
                  {faa.classification && faa.workflows && (
                    <Tooltip
                      title="Vehicle Classification"
                      placement="right"
                      arrow
                      disableHoverListener={isOpen}
                    >
                      <ListItem
                        button
                        component={Link}
                        onClick={handleNavClick}
                        to={
                          currentProjectId
                            ? `/project/${currentProjectId}/classification-analysis`
                            : "#"
                        }
                        selected={location.pathname.includes("/classification-analysis")}
                        sx={{ px: isOpen ? 2 : "14px" }}
                      >
                        <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                          <FlightTakeoffIcon />
                        </ListItemIcon>
                        {isOpen && <ListItemText primary="Classification" />}
                      </ListItem>
                    </Tooltip>
                  )}

                  {/* Workflows - Operators Only */}
                  {faa.workflows && (
                    <Tooltip
                      title="My Workflows"
                      placement="right"
                      arrow
                      disableHoverListener={isOpen}
                    >
                      <ListItem
                        button
                        component={Link}
                        onClick={handleNavClick}
                        to={currentProjectId ? `/project/${currentProjectId}/process-flow` : "#"}
                        selected={location.pathname.includes("/process-flow")}
                        sx={{ px: isOpen ? 2 : "14px" }}
                      >
                        <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                          <AccountTreeIcon />
                        </ListItemIcon>
                        {isOpen && <ListItemText primary="My Workflows" />}
                      </ListItem>
                    </Tooltip>
                  )}

                  {/* Admin Review - Admins Only */}
                  {faa.adminReview && (
                    <Tooltip
                      title="Pending Submissions"
                      placement="right"
                      arrow
                      disableHoverListener={isOpen}
                    >
                      <ListItem
                        button
                        component={Link}
                        onClick={handleNavClick}
                        to={
                          currentProjectId ? `/project/${currentProjectId}/admin/submissions` : "#"
                        }
                        selected={location.pathname.includes("/admin/submissions")}
                        sx={{ px: isOpen ? 2 : "14px" }}
                      >
                        <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                          <PendingActionsIcon />
                        </ListItemIcon>
                        {isOpen && <ListItemText primary="Submissions" />}
                      </ListItem>
                    </Tooltip>
                  )}

                  {/* Gap Analysis */}
                  {faa.gapAnalysis && (
                    <Tooltip
                      title="Gap Analysis"
                      placement="right"
                      arrow
                      disableHoverListener={isOpen}
                    >
                      <ListItem
                        button
                        component={Link}
                        onClick={handleNavClick}
                        to={currentProjectId ? `/project/${currentProjectId}/gap-analysis` : "#"}
                        selected={location.pathname.includes("/gap-analysis")}
                        sx={{ px: isOpen ? 2 : "14px" }}
                      >
                        <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                          <CompareArrowsIcon />
                        </ListItemIcon>
                        {isOpen && <ListItemText primary="Gap Analysis" />}
                      </ListItem>
                    </Tooltip>
                  )}

                  {/* DRS Query */}
                  {faa.drs && (
                    <Tooltip
                      title="DRS Query"
                      placement="right"
                      arrow
                      disableHoverListener={isOpen}
                    >
                      <ListItem
                        button
                        component={Link}
                        onClick={handleNavClick}
                        to={currentProjectId ? `/project/${currentProjectId}/drs-query` : "#"}
                        selected={location.pathname.includes("/drs-query")}
                        sx={{ px: isOpen ? 2 : "14px" }}
                      >
                        <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                          <GavelIcon />
                        </ListItemIcon>
                        {isOpen && <ListItemText primary="DRS Query" />}
                      </ListItem>
                    </Tooltip>
                  )}

                  <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.08)" }} />
                </>
              )}

              {/* Automotive Certification Section - Gated by Domain */}
              {automotive.enabled && (
                <>
                  <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.08)" }} />
                  {isOpen && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ px: 2, py: 1, display: "block", fontWeight: 700 }}
                    >
                      AUTOMOTIVE CERTIFICATION
                    </Typography>
                  )}

                  {/* Vehicle Classification */}
                  {automotive.classification && (
                    <Tooltip
                      title="Vehicle Classification"
                      placement="right"
                      arrow
                      disableHoverListener={isOpen}
                    >
                      <ListItem
                        button
                        component={Link}
                        onClick={handleNavClick}
                        to={
                          currentProjectId
                            ? `/project/${currentProjectId}/automotive/classification`
                            : "#"
                        }
                        selected={location.pathname.includes("/automotive/classification")}
                        sx={{ px: isOpen ? 2 : "14px" }}
                      >
                        <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                          <DirectionsCarIcon />
                        </ListItemIcon>
                        {isOpen && <ListItemText primary="Classification" />}
                      </ListItem>
                    </Tooltip>
                  )}

                  <Divider sx={{ my: 1, borderColor: "rgba(0,0,0,0.08)" }} />
                </>
              )}

              <Tooltip
                title="Agent Applicator"
                placement="right"
                arrow
                disableHoverListener={isOpen}
              >
                <ListItemButton onClick={handleApplicatorClick}>
                  {isOpen && (
                    <>
                      <ListItemText
                        primary="AGENT APPLICATOR"
                        primaryTypographyProps={{
                          className: "agent-group-item",
                        }}
                      />
                      {applicatorOpen ? <ExpandLess /> : <ExpandMore />}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>

              <Collapse in={applicatorOpen} timeout="auto">
                <List component="div" disablePadding>
                  <Tooltip
                    title="Applications"
                    placement="right"
                    arrow
                    disableHoverListener={isOpen}
                  >
                    <ListItem
                      button
                      component={Link}
                      onClick={handleNavClick}
                      to={currentProjectId ? `/project/${currentProjectId}/applications` : "#"}
                      selected={location.pathname === `/project/${currentProjectId}/applications`}
                      sx={{ px: isOpen ? 2 : "14px" }}
                    >
                      <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                        <ScienceIcon />
                      </ListItemIcon>
                      {isOpen && <ListItemText primary="Applications" />}
                    </ListItem>
                  </Tooltip>
                  <Tooltip
                    title="Traceability"
                    placement="right"
                    arrow
                    disableHoverListener={isOpen}
                  >
                    <ListItem
                      button
                      component={Link}
                      onClick={handleNavClick}
                      to={currentProjectId ? `/project/${currentProjectId}/trace` : "#"}
                      selected={location.pathname === `/project/${currentProjectId}/trace`}
                      sx={{
                        px: isOpen ? 2 : "14px",
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                        <InsightsIcon />
                      </ListItemIcon>
                      {isOpen && <ListItemText primary="Traceability" />}
                    </ListItem>
                  </Tooltip>
                </List>
              </Collapse>

              <Tooltip title="Agent Approver" placement="right" arrow disableHoverListener={isOpen}>
                <ListItemButton onClick={handleApproverClick}>
                  {isOpen && (
                    <>
                      <ListItemText
                        primary="AGENT APPROVER"
                        primaryTypographyProps={{
                          className: "agent-group-item",
                        }}
                      />
                      {approverOpen ? <ExpandLess /> : <ExpandMore />}
                    </>
                  )}
                </ListItemButton>
              </Tooltip>

              <Collapse in={approverOpen} timeout="auto">
                <List component="div" disablePadding>
                  <Tooltip
                    title={isDemoMode ? "Approvals" : "Approvals (Soon)"}
                    placement="right"
                    arrow
                    disableHoverListener={isOpen}
                  >
                    <NotDevelopedItem>
                      <ListItem
                        button
                        component={Link}
                        onClick={handleNavClick}
                        to={currentProjectId ? `/project/${currentProjectId}/approvals` : "#"}
                        selected={location.pathname === `/project/${currentProjectId}/approvals`}
                        sx={{
                          px: isOpen ? 2 : "14px",
                          cursor: "not-allowed",
                          "&.Mui-disabled": {
                            opacity: 1,
                            pointerEvents: "none",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                          <AssignmentIcon />
                        </ListItemIcon>
                        {isOpen && (
                          <ListItemText
                            primary={
                              isDemoMode ? (
                                "Approvals"
                              ) : (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <span>Approvals</span>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 1, fontSize: "0.7rem" }}
                                  >
                                    (Soon)
                                  </Typography>
                                </Box>
                              )
                            }
                          />
                        )}
                      </ListItem>
                    </NotDevelopedItem>
                  </Tooltip>
                  <Tooltip
                    title={isDemoMode ? "Followups" : "Followups (Soon)"}
                    placement="right"
                    arrow
                    disableHoverListener={isOpen}
                  >
                    <NotDevelopedItem>
                      <ListItem
                        button
                        component={Link}
                        onClick={handleNavClick}
                        to={currentProjectId ? `/project/${currentProjectId}/followups` : "#"}
                        selected={location.pathname === `/project/${currentProjectId}/followups`}
                        sx={{
                          px: isOpen ? 2 : "14px",
                          cursor: "not-allowed",
                          "&.Mui-disabled": {
                            opacity: 1,
                            pointerEvents: "none",
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                          <HistoryIcon />
                        </ListItemIcon>
                        {isOpen && (
                          <ListItemText
                            primary={
                              isDemoMode ? (
                                "Followups"
                              ) : (
                                <Box sx={{ display: "flex", alignItems: "center" }}>
                                  <span>Followups</span>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 1, fontSize: "0.7rem" }}
                                  >
                                    (Soon)
                                  </Typography>
                                </Box>
                              )
                            }
                          />
                        )}
                      </ListItem>
                    </NotDevelopedItem>
                  </Tooltip>
                </List>
              </Collapse>

              <Divider sx={{ my: 1 }} />

              <Tooltip
                title="Executive Report"
                placement="right"
                arrow
                disableHoverListener={isOpen}
              >
                <ListItem
                  button
                  component={Link}
                  onClick={handleNavClick}
                  to={currentProjectId ? `/project/${currentProjectId}/executive-report` : "#"}
                  selected={location.pathname === `/project/${currentProjectId}/executive-report`}
                  sx={{ px: isOpen ? 2 : "14px" }}
                >
                  <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                    <AssignmentIcon />
                  </ListItemIcon>
                  {isOpen && <ListItemText primary="Executive Report" />}
                </ListItem>
              </Tooltip>

              {featureFlags.SUPPLIER_PORTAL_ENABLED && (
                <Tooltip title="Suppliers" placement="right" arrow disableHoverListener={isOpen}>
                  <ListItem
                    button
                    component={Link}
                    onClick={handleNavClick}
                    to={currentProjectId ? `/project/${currentProjectId}/suppliers` : "#"}
                    selected={
                      location.pathname.startsWith("/suppliers") ||
                      location.pathname.startsWith("/supplier-tasks") ||
                      location.pathname.startsWith("/parent-tasks")
                    }
                    sx={{ px: isOpen ? 2 : "14px" }}
                  >
                    <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                      <BusinessIcon />
                    </ListItemIcon>
                    {isOpen && <ListItemText primary="Suppliers" />}
                  </ListItem>
                </Tooltip>
              )}

              <Tooltip
                title={isDemoMode ? "Audit Reports" : "Audit Reports (Soon)"}
                placement="right"
                arrow
                disableHoverListener={isOpen}
              >
                <NotDevelopedItem>
                  <ListItem
                    button
                    component={Link}
                    onClick={handleNavClick}
                    to={currentProjectId ? `/project/${currentProjectId}/compliance` : "#"}
                    selected={location.pathname === `/project/${currentProjectId}/compliance`}
                    sx={{
                      px: isOpen ? 2 : "14px",
                      cursor: "not-allowed",
                      "&.Mui-disabled": {
                        opacity: 1,
                        pointerEvents: "none",
                      },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                      <SecurityIcon />
                    </ListItemIcon>
                    {isOpen && (
                      <ListItemText
                        primary={
                          isDemoMode ? (
                            "Audit Reports"
                          ) : (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <span>Audit Reports</span>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ ml: 1, fontSize: "0.7rem" }}
                              >
                                (Soon)
                              </Typography>
                            </Box>
                          )
                        }
                      />
                    )}
                  </ListItem>
                </NotDevelopedItem>
              </Tooltip>

              <Tooltip title="Documents" placement="right" arrow disableHoverListener={isOpen}>
                <ListItem
                  button
                  component={currentProjectId ? Link : "div"}
                  to={currentProjectId ? `/project/${currentProjectId}/settings` : undefined}
                  onClick={(e) => {
                    if (!currentProjectId) {
                      e.preventDefault();
                      console.warn("No project selected");
                    }
                  }}
                  selected={
                    currentProjectId &&
                    location.pathname === `/project/${currentProjectId}/settings`
                  }
                  sx={{
                    px: isOpen ? 2 : "14px",
                    opacity: currentProjectId ? 1 : 0.5,
                    cursor: currentProjectId ? "pointer" : "not-allowed",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                    <DescriptionIcon />
                  </ListItemIcon>
                  {isOpen && <ListItemText primary="Documents" />}
                </ListItem>
              </Tooltip>

              <Tooltip
                title="Knowledge Graph"
                placement="right"
                arrow
                disableHoverListener={isOpen}
              >
                <ListItem
                  button
                  component={Link}
                  to="/knowledge-graph"
                  selected={location.pathname === "/knowledge-graph"}
                  sx={{ px: isOpen ? 2 : "14px" }}
                >
                  <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                    <AccountTreeIcon />
                  </ListItemIcon>
                  {isOpen && <ListItemText primary="Knowledge Graph" />}
                </ListItem>
              </Tooltip>

              <Tooltip title="Google Drive" placement="right" arrow disableHoverListener={isOpen}>
                <ListItem
                  button
                  component={Link}
                  to="/settings/google-drive"
                  selected={location.pathname === "/settings/google-drive"}
                  sx={{ px: isOpen ? 2 : "14px" }}
                >
                  <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                    <CloudUploadIcon />
                  </ListItemIcon>
                  {isOpen && <ListItemText primary="Google Drive" />}
                </ListItem>
              </Tooltip>

              <Divider sx={{ my: 1 }} />
              <Tooltip
                title="Raise a Request"
                placement="right"
                arrow
                disableHoverListener={isOpen}
              >
                <ListItem
                  button
                  component={Link}
                  to="/support-requests"
                  selected={location.pathname === "/support-requests"}
                  sx={{ px: isOpen ? 2 : "14px" }}
                >
                  <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                    <SupportAgentIcon />
                  </ListItemIcon>
                  {isOpen && <ListItemText primary="Raise a Request" />}
                </ListItem>
              </Tooltip>

              {/* Admin: All Requests - CertBuddy Only */}
              {isCertBuddyMember(user) && (
                <Tooltip title="All Requests" placement="right" arrow disableHoverListener={isOpen}>
                  <ListItem
                    button
                    component={Link}
                    to="/admin/support-requests"
                    selected={location.pathname === "/admin/support-requests"}
                    sx={{ px: isOpen ? 2 : "14px" }}
                  >
                    <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                      <ListAltIcon />
                    </ListItemIcon>
                    {isOpen && <ListItemText primary="All Requests" />}
                  </ListItem>
                </Tooltip>
              )}

              {/* Admin: Agent Observability (Langfuse traces) - CertBuddy Only */}
              {isCertBuddyMember(user) && (
                <Tooltip
                  title="Agent Observability"
                  placement="right"
                  arrow
                  disableHoverListener={isOpen}
                >
                  <ListItem
                    button
                    component={Link}
                    to="/admin/observability"
                    selected={location.pathname === "/admin/observability"}
                    sx={{ px: isOpen ? 2 : "14px" }}
                  >
                    <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                      <InsightsIcon />
                    </ListItemIcon>
                    {isOpen && <ListItemText primary="Agent Observability" />}
                  </ListItem>
                </Tooltip>
              )}

              {/* Admin: CertGPT evals (answer keys, runs, chunk quality) - CertBuddy Only */}
              {isCertBuddyMember(user) && (
                <Tooltip
                  title="CertGPT Evals"
                  placement="right"
                  arrow
                  disableHoverListener={isOpen}
                >
                  <ListItem
                    button
                    component={Link}
                    to="/admin/evals"
                    selected={location.pathname === "/admin/evals"}
                    sx={{ px: isOpen ? 2 : "14px" }}
                  >
                    <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                      <FactCheckIcon />
                    </ListItemIcon>
                    {isOpen && <ListItemText primary="CertGPT Evals" />}
                  </ListItem>
                </Tooltip>
              )}

              {/* Admin: Missing Documents (per workflow) - CertBuddy Only */}
              {isCertBuddyMember(user) && (
                <Tooltip
                  title="Missing Documents"
                  placement="right"
                  arrow
                  disableHoverListener={isOpen}
                >
                  <ListItem
                    button
                    component={Link}
                    to="/admin/missing-documents"
                    selected={location.pathname === "/admin/missing-documents"}
                    sx={{ px: isOpen ? 2 : "14px" }}
                  >
                    <ListItemIcon sx={{ minWidth: isOpen ? 40 : "auto" }}>
                      <ErrorIcon />
                    </ListItemIcon>
                    {isOpen && <ListItemText primary="Missing Documents" />}
                  </ListItem>
                </Tooltip>
              )}
            </>
          )}
        </List>

        {/* Collapse Button */}
        <Box
          sx={{
            borderTop: "1px solid #e0e0e0",
            p: 0.5,
            display: "flex",
            justifyContent: "center",
            minHeight: "40px",
          }}
        >
          <IconButton onClick={onToggle} size="small">
            <ChevronLeftIcon
              sx={{
                transform: isOpen ? "none" : "rotate(180deg)",
                transition: "transform 0.2s",
              }}
            />
          </IconButton>
        </Box>
      </Drawer>

      <ProjectChangeModal
        open={projectChangeModal.open}
        onClose={() => setProjectChangeModal({ open: false, project: null })}
        onConfirm={handleProjectChangeConfirm}
        project={projectChangeModal.project}
      />

      <SelectProjectPrompt
        open={showSelectProjectPrompt}
        onClose={() => setShowSelectProjectPrompt(false)}
      />
    </>
  );
}

export default Sidebar;
